import { useEffect, useState } from 'react';
import { Box, Typography, Button, Paper, IconButton, Dialog, DialogContent, TextField, FormControlLabel, Checkbox, Chip, Grid, FormControl, InputLabel, Select, MenuItem, Divider } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import supabase from '../../../supabaseClient';
import { useNavigate } from 'react-router-dom';

const emptyEditForm = {
    username: '',
    profile_type: 'worker',
};

// Moduły panelu Biura — te same klucze co `key` w ManagerNavBar.js navItems,
// żeby profiles.permissions dało się bezpośrednio porównywać z item.key przy
// filtrowaniu menu w NavShell.js. Produkcja/Kierowca nie mają jeszcze własnych
// modułów do granularnego nadawania (patrz NavShell.js — hasModulePermissions).
const MANAGER_MODULES = [
    { key: 'home', label: 'Home' },
    { key: 'orders', label: 'Zamówienia' },
    { key: 'warehouse', label: 'Magazyn' },
    { key: 'gatekeeper', label: 'Obecności' },
    { key: 'settlements', label: 'Rozliczenia' },
    { key: 'administration', label: 'Ustawienia' },
    { key: 'reports', label: 'Raporty' },
];
const MANAGER_MODULE_KEYS = MANAGER_MODULES.map((m) => m.key);

const toggleModule = (permissions, setPermissions, key) => {
    setPermissions(
        permissions.includes(key) ? permissions.filter((k) => k !== key) : [...permissions, key]
    );
};

// Checkboxy modułów Biura — pokazywane tylko gdy wybrana rola to 'manager'.
// Produkcja/Kierowca nie mają jeszcze własnych modułów do nadawania.
function ModulePermissionsFields({ permissions, setPermissions }) {
    return (
        <Box sx={{ border: '1px solid #ECE7DE', borderRadius: 2, p: 1.5 }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
                Dostępne moduły
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                {MANAGER_MODULES.map((m) => (
                    <FormControlLabel
                        key={m.key}
                        sx={{ width: { xs: '100%', sm: '50%' }, mr: 0 }}
                        control={
                            <Checkbox
                                size="small"
                                checked={permissions.includes(m.key)}
                                onChange={() => toggleModule(permissions, setPermissions, m.key)}
                            />
                        }
                        label={m.label}
                    />
                ))}
            </Box>
        </Box>
    );
}

function ManagerUsers() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const [idConfiguration, setIdConfiguration] = useState(null);
    const [showActive, setShowActive] = useState(true);
    const [showBlocked, setShowBlocked] = useState(false);

    const [filterOpen, setFilterOpen] = useState(false);
    const [searchName, setSearchName] = useState('');
    const [searchEmail, setSearchEmail] = useState('');
    const [searchID, setSearchID] = useState('');

    // Stany dla okna dialogowego dodawania nowego użytkownika
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newUsername, setNewUsername] = useState('');
    const [newProfileType, setNewProfileType] = useState('worker');
    const [newPermissions, setNewPermissions] = useState(MANAGER_MODULE_KEYS);
    const [formError, setFormError] = useState(null);
    const [adding, setAdding] = useState(false);

    // Snackbar dla komunikatów
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('Użytkownik został pomyślnie utworzony!');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // Stany dla okna dialogowego edycji użytkownika
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editForm, setEditForm] = useState(emptyEditForm);
    const [editPermissions, setEditPermissions] = useState(MANAGER_MODULE_KEYS);
    const [isBlocked, setIsBlocked] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editErrorMsg, setEditErrorMsg] = useState('');

    // Stany dla zmiany hasła w dialogu edycji
    const [newPasswordEdit, setNewPasswordEdit] = useState('');
    const [passwordError, setPasswordError] = useState(null);
    const [passwordSuccess, setPasswordSuccess] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        const getInitialData = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
                const { data: profileData } = await supabase
                    .from('profiles')
                    .select('id_configuration')
                    .eq('id', session.user.id)
                    .single();

                if (profileData) {
                    setIdConfiguration(profileData.id_configuration);
                }
            }
        };
        getInitialData();
    }, []);

    async function fetchUsers() {
        if (!idConfiguration) return;
        setLoading(true);

        const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id_configuration', idConfiguration);

        if (error) {
            console.error('Błąd pobierania użytkowników:', error);
            setLoading(false);
            return;
        }

        setRows(data || []);
        setLoading(false);
    }

    useEffect(() => {
        if (idConfiguration) {
            fetchUsers();
        }
    }, [idConfiguration]);

    const handleAddSubmit = async () => {
        setFormError(null);

        if (!newEmail.trim() || !newPassword.trim() || !newUsername.trim()) {
            setFormError('Wszystkie pola są wymagane.');
            return;
        }

        setAdding(true);

        try {
            const { data: sessionData } = await supabase.auth.getSession();
            const token = sessionData?.session?.access_token;

            const response = await fetch(
                `https://${supabase.supabaseUrl.split('//')[1]}/functions/v1/bright-action`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token || supabase.supabaseKey}`,
                        'apikey': supabase.supabaseKey
                    },
                    body: JSON.stringify({
                        email: newEmail,
                        password: newPassword,
                        username: newUsername,
                        profile_type: newProfileType,
                        id_configuration: idConfiguration
                    })
                }
            );

            const result = await response.json();

            if (!response.ok) {
                const errorMessage = result.error || "Wystąpił błąd serwera";
                if (errorMessage.includes('already registered') || errorMessage.includes('User already registered') || errorMessage.includes('already been registered')) {
                    setFormError("Użytkownik o takim adresie e-mail już istnieje!");
                } else {
                    setFormError("Błąd: " + errorMessage);
                }
                setAdding(false);
                return;
            }

            // Edge function 'bright-action' tworzy wiersz profiles po stronie serwera i nie
            // zwraca jego id — dla roli Biuro doszukujemy się świeżo utworzonego konta po
            // (email, id_configuration) i donadajemy mu wybrane moduły. Best-effort: jeśli się
            // nie uda, konto i tak istnieje z pełnym dostępem (permissions=null), da się to
            // poprawić później w edycji.
            if (newProfileType === 'manager') {
                const { data: createdRow } = await supabase
                    .from('profiles')
                    .select('id')
                    .eq('id_configuration', idConfiguration)
                    .eq('full_name', newEmail)
                    .order('id', { ascending: false })
                    .limit(1)
                    .maybeSingle();
                if (createdRow) {
                    await supabase.from('profiles').update({ permissions: newPermissions }).eq('id', createdRow.id);
                }
            }

            setNewEmail('');
            setNewPassword('');
            setNewUsername('');
            setNewProfileType('worker');
            setNewPermissions(MANAGER_MODULE_KEYS);
            setSnackbarMessage('Użytkownik został pomyślnie utworzony!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setAddDialogOpen(false);
            setAdding(false);

            fetchUsers();

        } catch (err) {
            setFormError("Wystąpił nieoczekiwany błąd sieciowy.");
            console.error(err);
            setAdding(false);
        }
    };

    const openEditDialog = (row) => {
        setEditingUser(row);
        setEditForm({
            username: row.username ?? '',
            profile_type: row.profile_type ?? 'worker',
        });
        // null/puste permissions = pełen dostęp (patrz NavShell.js) — w formularzu
        // pokazujemy to jako "wszystko zaznaczone", żeby admin widział rzeczywisty
        // efektywny stan, a nie pustą listę
        setEditPermissions(
            Array.isArray(row.permissions) && row.permissions.length > 0
                ? row.permissions
                : MANAGER_MODULE_KEYS
        );
        setIsBlocked(row.isBlocked !== null);
        setEditErrorMsg('');
        setNewPasswordEdit('');
        setPasswordError(null);
        setPasswordSuccess(false);
        setEditDialogOpen(true);
    };

    const handleEditSave = async () => {
        if (!editForm.username.trim()) {
            setEditErrorMsg('Imię i nazwisko jest wymagane.');
            return;
        }

        setSaving(true);
        setEditErrorMsg('');

        const { error } = await supabase
            .from('profiles')
            .update({
                username: editForm.username.trim(),
                profile_type: editForm.profile_type,
                isBlocked: isBlocked ? 1 : null,
                permissions: editForm.profile_type === 'manager' ? editPermissions : null,
            })
            .eq('id', editingUser.id);
        
        if (error) {
            console.error("Błąd podczas zapisu:", error);
            setEditErrorMsg(`Wystąpił błąd zapisu: ${error.message}`);
            setSaving(false);
            return;
        }

        setSaving(false);
        setSnackbarMessage("Zaktualizowano pomyślnie!");
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setEditDialogOpen(false);
        fetchUsers();
    };

    const handlePasswordChange = async (event) => {
        event.preventDefault();
        setPasswordError(null);
        setPasswordSuccess(false);
        setChangingPassword(true);

        try {
            const { data: sessionData } = await supabase.auth.getSession();
            const token = sessionData?.session?.access_token;

            const response = await fetch(
                `https://${supabase.supabaseUrl.split('//')[1]}/functions/v1/change-user-password`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token || supabase.supabaseKey}`,
                        'apikey': supabase.supabaseKey
                    },
                    body: JSON.stringify({
                        userId: editingUser.id,
                        newPassword: newPasswordEdit
                    })
                }
            );

            const result = await response.json();

            if (!response.ok) {
                setPasswordError("Błąd: " + (result.error || "Nie udało się zmienić hasła"));
                setChangingPassword(false);
                return;
            }

            setPasswordSuccess(true);
            setNewPasswordEdit('');
        } catch (err) {
            setPasswordError("Wystąpił nieoczekiwany błąd sieciowy.");
            console.error(err);
        }
        setChangingPassword(false);
    };

    const filteredRows = rows.filter((row) => {
        const matchesName = searchName === '' || (row.username && row.username.toLowerCase().includes(searchName.toLowerCase()));
        const matchesID = searchID === '' || (row.id && row.id.toString().toLowerCase().includes(searchID.toLowerCase()));
        const matchesEmail = searchEmail === '' || (row.full_name && row.full_name.toLowerCase().includes(searchEmail.toLowerCase()));
        
        let matchesStatus = false;
        if (showActive && row.isBlocked !== 1) matchesStatus = true;
        if (showBlocked && row.isBlocked === 1) matchesStatus = true;

        return matchesName && matchesID && matchesEmail && matchesStatus;
    });

    const formatProfileType = (profileType) => {
        switch (profileType) {
            case 'client': return 'Client';
            case 'worker': return 'Produkcja';
            case 'manager': return 'Biuro';
            case 'driver': return 'Kierowca';
            default: return profileType;
        }
    };

    const columns = [
        { field: 'id', headerName: 'ID', flex: 1, minWidth: 150, renderCell: (params) => <strong>{params.value}</strong> },
        { field: 'username', headerName: 'IMIĘ I NAZWISKO', flex: 1.2, minWidth: 180, renderCell: (params) => <strong>{params.value}</strong> },
        { field: 'full_name', headerName: 'E-MAIL / INFO', flex: 1.2, minWidth: 180 },
        {
            field: 'profile_type',
            headerName: 'TYP',
            flex: 0.9,
            minWidth: 130,
            renderCell: (params) => formatProfileType(params.value)
        },
        {
            field: 'isBlocked',
            headerName: 'STATUS',
            flex: 0.8,
            minWidth: 110,
            renderCell: (params) => (
                <Chip
                    label={params.value === 1 ? 'Zablokowany' : 'Aktywny'}
                    size="small"
                    sx={{
                        bgcolor: params.value === 1 ? '#F1EDE6' : '#E4EDFB',
                        color: params.value === 1 ? '#6B6B6B' : '#2E6DC4',
                        fontWeight: 600,
                        borderRadius: '8px',
                        height: 26
                    }}
                />
            ),
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.6,
            minWidth: 90,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditDialog(params.row); }}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: 'primary.main' }}>Użytkownicy</Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setFilterOpen(true)}
                        startIcon={<FilterListIcon />}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2, py: 1 }}
                    >
                        Filtry
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setNewEmail('');
                            setNewPassword('');
                            setNewUsername('');
                            setNewProfileType('worker');
                            setNewPermissions(MANAGER_MODULE_KEYS);
                            setFormError(null);
                            setAddDialogOpen(true);
                        }}
                        startIcon={<AddIcon />}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5, py: 1 }}
                    >
                        Nowy użytkownik
                    </Button>
                </Box>
            </Box>

            <Snackbar open={snackbarOpen} autoHideDuration={2000} onClose={() => setSnackbarOpen(false)}>
                <Alert severity={snackbarSeverity}>{snackbarMessage}</Alert>
            </Snackbar>

            <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #ECE7DE' }}>
                <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    loading={loading}
                    onRowClick={(params) => openEditDialog(params.row)}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    hideFooter={filteredRows.length <= 100}
                    columnHeaderHeight={44}
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-row': { cursor: 'pointer' },
                        '& .MuiDataGrid-columnHeaders': { backgroundColor: '#FAF7F2' },
                        '& .MuiDataGrid-columnHeaderTitle': { color: 'text.secondary', fontWeight: 700, fontSize: '12px', letterSpacing: '0.03em' },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-row:hover': { backgroundColor: '#FEFCF9' },
                    }}
                />
            </Paper>

            {/* Okno dialogowe dodawania nowego użytkownika */}
            <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent sx={{ p: 4 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'primary.main', mb: 3 }}>
                        Dodaj nowego użytkownika
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                        {formError && <Alert severity="error">{formError}</Alert>}
                        <TextField
                            name="email"
                            type="email"
                            label="E-mail"
                            size="small"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            fullWidth
                            required
                        />
                        <TextField
                            name="password"
                            type="password"
                            label="Hasło"
                            size="small"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            fullWidth
                            required
                        />
                        <TextField
                            name="username"
                            label="Imię i nazwisko"
                            size="small"
                            value={newUsername}
                            onChange={(e) => setNewUsername(e.target.value)}
                            fullWidth
                            required
                        />
                        <FormControl fullWidth size="small">
                            <InputLabel id="status-select-label">Typ profilu</InputLabel>
                            <Select
                                labelId="status-select-label"
                                name="profile_type"
                                label="Typ profilu"
                                value={newProfileType}
                                onChange={(e) => setNewProfileType(e.target.value)}
                                required
                            >
                                <MenuItem value="manager">Biuro</MenuItem>
                                <MenuItem value="worker">Produkcja</MenuItem>
                                <MenuItem value="driver">Kierowca</MenuItem>
                            </Select>
                        </FormControl>
                        {newProfileType === 'manager' && (
                            <ModulePermissionsFields permissions={newPermissions} setPermissions={setNewPermissions} />
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button onClick={() => setAddDialogOpen(false)} disabled={adding} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            Anuluj
                        </Button>
                        <Button variant="contained" onClick={handleAddSubmit} disabled={adding} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            {adding ? 'Tworzenie...' : 'Utwórz'}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Okno dialogowe edycji użytkownika */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent sx={{ p: 4 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'primary.main', mb: 3 }}>
                        Edycja użytkownika
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                        <TextField
                            label="Email"
                            size="small"
                            value={editingUser?.full_name || ''}
                            fullWidth
                            disabled
                        />
                        <TextField
                            label="Imię i nazwisko"
                            size="small"
                            value={editForm.username}
                            onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                            fullWidth
                            required
                        />
                        <FormControl fullWidth size="small">
                            <InputLabel id="dialog-profile-type-label">Typ profilu</InputLabel>
                            <Select
                                labelId="dialog-profile-type-label"
                                label="Typ profilu"
                                value={editForm.profile_type}
                                onChange={(e) => setEditForm({ ...editForm, profile_type: e.target.value })}
                                required
                            >
                                <MenuItem value="manager">Biuro</MenuItem>
                                <MenuItem value="worker">Produkcja</MenuItem>
                                <MenuItem value="driver">Kierowca</MenuItem>
                            </Select>
                        </FormControl>
                        {editForm.profile_type === 'manager' && (
                            <ModulePermissionsFields permissions={editPermissions} setPermissions={setEditPermissions} />
                        )}
                        <TextField
                            label="ID"
                            size="small"
                            value={editingUser?.id || ''}
                            fullWidth
                            disabled
                        />
                        <FormControlLabel 
                            control={
                                <Checkbox
                                    checked={isBlocked} 
                                    onChange={(e) => setIsBlocked(e.target.checked)}
                                    color="primary"
                                />
                            } 
                            label="Zablokowany" 
                        />
                        {editErrorMsg && <Typography sx={{ color: '#C7433F', fontSize: '13px' }}>{editErrorMsg}</Typography>}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mb: 3 }}>
                        <Button onClick={() => setEditDialogOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            Anuluj
                        </Button>
                        <Button variant="contained" onClick={handleEditSave} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            {saving ? 'Zapisywanie...' : 'Zapisz'}
                        </Button>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'primary.main', mb: 2 }}>
                        Zmiana hasła użytkownika
                    </Typography>

                    <form onSubmit={handlePasswordChange}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                type="password"
                                label="Nowe hasło"
                                size="small"
                                value={newPasswordEdit}
                                onChange={(e) => setNewPasswordEdit(e.target.value)}
                                fullWidth
                                required
                            />
                            <Box display="flex" justifyContent="flex-end">
                                <Button
                                    type="submit"
                                    variant="outlined"
                                    color="secondary"
                                    disabled={changingPassword}
                                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}
                                >
                                    {changingPassword ? 'Zmienianie...' : 'Zmień hasło'}
                                </Button>
                            </Box>
                            {passwordError && <Alert severity="error">{passwordError}</Alert>}
                            {passwordSuccess && <Alert severity="success">Hasło zostało pomyślnie zmienione!</Alert>}
                        </Box>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Okno dialogowe filtrów */}
            <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent sx={{ p: 4 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'primary.main', mb: 3 }}>
                        Filtry
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                        <TextField
                            label="ID"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchID}
                            onChange={(e) => setSearchID(e.target.value)}
                        />
                        <TextField
                            label="Imię"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                        />
                        <TextField
                            label="E-mail"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchEmail}
                            onChange={(e) => setSearchEmail(e.target.value)}
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControlLabel
                                control={<Checkbox checked={showActive} onChange={(e) => setShowActive(e.target.checked)} color="primary" />}
                                label="Aktywni"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={showBlocked} onChange={(e) => setShowBlocked(e.target.checked)} color="primary" />}
                                label="Nieaktywni"
                            />
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button onClick={() => setFilterOpen(false)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            Zamknij
                        </Button>
                        <Button variant="contained" onClick={() => setFilterOpen(false)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            Zastosuj
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default ManagerUsers;